import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    IndividualTaxDetailsService,
    IndividualTaxDetailInterface,
    CountryModel,
    IndividualExtendedModel,
    IndividualTaxDetailModel,
    IndividualTaxDetailInputModel,
} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator, MaxLengthValidatorKey} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper} from '../../../../shared/classes';

import {TaxDetailsFormErrorStatesEnum} from '../../enums/error-states';

import {
    TaxDetailControls,
    TaxDetailIdSelector,
    TaxDetailCountryIdSelector,
    TaxDetailTaxNumberSelector,
    TaxDetailsFormArraySelector,
    TaxDetailTaxNumberMaxLength,
} from '../../../../../assets/descriptors';

export class TaxDetailsForm {
    private readonly countries: CountryModel[];
    private readonly taxDetailsService: IndividualTaxDetailsService;

    public form: FormGroup;
    public individual: IndividualExtendedModel;
    public controls: Array<BaseControl<string | number>[]>;
    public controlsObservable: BehaviorSubject<Array<BaseControl<string | number>[]>>;

    public readonly taxDetailsFormArraySelector: string;

    constructor(
        individual: IndividualExtendedModel,
        countries: CountryModel[],
        taxDetailsService: IndividualTaxDetailsService
    ) {
        this.individual = individual;
        this.countries = !countries ? [] : countries;
        this.taxDetailsService = taxDetailsService;

        this.taxDetailsFormArraySelector = TaxDetailsFormArraySelector;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private initControls(model?: IndividualTaxDetailModel): BaseControl[] {
        const controls = TaxDetailControls();

        return controls.map((control: BaseControl) => {
            if (model) {
                control.value = this.getTaxDetailProperty(control.name, model);
            }

            if (control.name === TaxDetailCountryIdSelector) {
                const countryIds = this.countries.map((country: CountryModel) => country.id);

                (control as CountryAutocompleteControl).countries = this.countries;

                control.extendValidators(InArrayValidator(countryIds));
            }

            return control;
        });
    }

    private getTaxDetailProperty(selector: string, model: IndividualTaxDetailModel): string | number {
        switch (selector) {
            case TaxDetailIdSelector:
                return model.id;
            case TaxDetailCountryIdSelector:
                return model.countryId;
            case TaxDetailTaxNumberSelector:
                return model.taxNumber;
            default:
                return null;
        }
    }

    private getFormControlStatuses(
        formGroup: FormGroup,
        formGroupIndex: number,
        formGroupCountryIds: string[],
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean[] {
        return Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
            const formControl = formGroup.controls[controlKey];
            const control = this.controls[formGroupIndex][controlIndex];

            if (formControl.invalid) {
                const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    if (errorKey === MaxLengthValidatorKey) {
                        return messageFunction(control.placeholder, TaxDetailTaxNumberMaxLength)[errorKey];
                    }

                    return messageFunction(control.placeholder)[errorKey];
                });

                control.setErrors(errors);

                return false;
            }

            if (control.name === TaxDetailCountryIdSelector) {
                const matchedIndex = formGroupCountryIds.indexOf(control.value as string);

                if ((matchedIndex !== formGroupIndex) && control.value) {
                    control.setError(messageFunction(control.placeholder)[DuplicatedValueValidatorKey]);

                    return false;
                }
            }

            return true;
        });
    }

    private removeEmptyGroups(emptyGroupIndexes: number[], formArray: FormArray): void {
        emptyGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);

            this.controls.splice(index, 1);
        });
    }

    private createUpdateRequest(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>> | null {
        const modelsForUpdate = formArray.controls
            .filter((formGroup: FormGroup) => {
                /**
                 * The filter below will return array from single key of identity detail id form control only
                 * if the form control has value different than null, this means that the whole form group
                 * is created for already existing identity detail.
                 */
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === TaxDetailIdSelector) &&
                        formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .filter(this.removeUnchangedTaxDetails.bind(this))
            .map(this.updateModels.bind(this));

        if (modelsForUpdate.length !== 0) {
            return this.taxDetailsService.updateMany(modelsForUpdate as IndividualTaxDetailModel[])
                .pipe(
                    switchMap(this.updateRequestMap.bind(this))
                );
        }

        return null;
    }

    private removeUnchangedTaxDetails(formGroup: FormGroup): boolean {
        const filteredTaxDetails = this.individual
            .taxDetails.filter((detail: IndividualTaxDetailModel) => {
                return detail.id === formGroup.controls[TaxDetailIdSelector].value;
            });

        if (filteredTaxDetails.length === 0) {
            return false;
        }

        const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
            const formControlValue = formGroup.controls[formControlKey].value;

            return this.checkTaxDetailProperty(
                formControlKey,
                formControlValue,
                filteredTaxDetails[0]
            );
        });

        return valuesCheck.indexOf(false) >= 0;
    }

    private checkTaxDetailProperty(
        selector: string,
        formPropertyValue: number | string,
        model: IndividualTaxDetailModel
    ): boolean {
        switch (selector) {
            case TaxDetailIdSelector:
                return model.id === formPropertyValue;
            case TaxDetailCountryIdSelector:
                return model.countryId === formPropertyValue;
            case TaxDetailTaxNumberSelector:
                return model.taxNumber === formPropertyValue;
            default:
                return false;
        }
    }

    private updateModels(formGroup: FormGroup): IndividualTaxDetailModel {
        const filteredTaxDetails = this.individual
            .taxDetails.filter((detail: IndividualTaxDetailModel) => {
                return detail.id === formGroup.controls[TaxDetailIdSelector].value;
            });

        filteredTaxDetails[0].countryId =
            formGroup.controls[TaxDetailCountryIdSelector].value;

        filteredTaxDetails[0].taxNumber =
            formGroup.controls[TaxDetailTaxNumberSelector].value;

        return filteredTaxDetails[0];
    }

    private updateRequestMap(
        updateResponse: ApiResponse<ApiError[] | null>
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        if (updateResponse.status !== HttpStatusCodesEnum.Ok) {
            return this.taxDetailsService.getByIndividualId(this.individual.id)
                .pipe(
                    map((getResponse: ApiResponse<ApiError[] | IndividualTaxDetailInterface[]>) => {
                        if (getResponse.status === HttpStatusCodesEnum.Ok) {
                            this.individual.taxDetails = (getResponse.data as IndividualTaxDetailInterface[])
                                .map((taxDetail: IndividualTaxDetailInterface) => new IndividualTaxDetailModel(taxDetail));
                        }

                        return Helper.errorsHelper().createErrorResponse(
                            updateResponse,
                            TaxDetailsFormErrorStatesEnum.UpdateDetailsFailed
                        );
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

    private createRegistrationRequest(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>> | null {
        const modelsForRegistration = formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === TaxDetailIdSelector) &&
                        !formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .map(this.createInputModels.bind(this));

        if (modelsForRegistration.length !== 0) {
            return this.taxDetailsService.registerMany(modelsForRegistration as IndividualTaxDetailInputModel[])
                .pipe(
                    map(this.registerRequestMap.bind(this))
                );
        }

        return null;
    }

    private createInputModels(formGroup: FormGroup): IndividualTaxDetailInputModel {
        return new IndividualTaxDetailInputModel(
            formGroup.controls[TaxDetailCountryIdSelector].value,
            formGroup.controls[TaxDetailTaxNumberSelector].value,
            this.individual.id
        );
    }

    private registerRequestMap(
        response: ApiResponse<ApiError[] | IndividualTaxDetailInterface[]>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualTaxDetailInterface[]>(
                response,
                TaxDetailsFormErrorStatesEnum.CreateDetailsFailed
            );
        }

        const models = (response.data as IndividualTaxDetailInterface[])
            .map((detail: IndividualTaxDetailInterface) => new IndividualTaxDetailModel(detail));

        if (!this.individual.taxDetails) {
            this.individual.taxDetails = [];
        }

        this.individual.taxDetails.push(...models);

        return Helper.createSuccessResponse<boolean>(true);
    }

    addTaxDetail(): void {
        if (this.controls.length < 9) {
            const controls = this.initControls().map((control: BaseControl) => {
                control.placeholder = control.placeholder + ' #' + this.controls.length;

                return control;
            });

            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            (this.form.controls[this.taxDetailsFormArraySelector] as FormArray).push(formGroup);

            this.controls.push(controls);
        }
    }

    canAddTaxDetail(): boolean {
        return this.controls && (this.controls.length < 9);
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.individual && this.individual.taxDetails && (this.individual.taxDetails.length !== 0)) {
            this.controls = this.individual.taxDetails.map((taxDetail: IndividualTaxDetailModel) => {
                return this.initControls(taxDetail);
            });
        } else {
            const controls = this.initControls();

            this.controls.push(controls);
        }

        const formArray = new FormArray([]);

        this.controls.map((controls: BaseControl[]) => {
            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            formArray.push(formGroup);
        });

        this.form.addControl(this.taxDetailsFormArraySelector, formArray);

        this.controlsObservable.next(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        const emptyFormGroupIndexes = [];
        const formArray = (this.form.controls[this.taxDetailsFormArraySelector] as FormArray);

        const formGroupCountryIds = formArray.controls.map((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey === TaxDetailCountryIdSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value);
        });

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey !== TaxDetailIdSelector)
                .map((controlKey: string) => !formGroup.controls[controlKey].value);

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            const formControlStatuses = this.getFormControlStatuses(
                formGroup,
                formGroupIndex,
                [].concat(...formGroupCountryIds),
                messageFunction
            );

            return formControlStatuses.indexOf(false) < 0;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            this.removeEmptyGroups(emptyFormGroupIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            const requests = [];
            const formArray = this.form.controls[this.taxDetailsFormArraySelector] as FormArray;

            if (this.individual.taxDetails && (this.individual.taxDetails.length !== 0)) {
                const updateRequest = this.createUpdateRequest(formArray);

                if (updateRequest) {
                    requests.push(updateRequest);
                }
            }

            const registrationRequest = this.createRegistrationRequest(formArray);

            if (registrationRequest) {
                requests.push(registrationRequest);
            }

            if (requests.length !== 0) {
                return forkJoin(requests)
                    .pipe(
                        map((responses: ApiResponse<ApiError[] | boolean>[]) => {
                            if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                                return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            }
        }

        return null;
    }
}
